import React, { useState, useContext, useMemo } from 'react';
import {
  Box, Button, Heading, Table, Thead, Tbody, Tr, Th, Td, Badge,
  Text, VStack, Spinner, useToast, Textarea, Flex, Link, Checkbox, HStack
} from "@chakra-ui/react";
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

const EvalCodingComparison = () => {
  const [caseIdsInput, setCaseIdsInput] = useState('');
  const [comparisonResults, setComparisonResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hideMatches, setHideMatches] = useState(false);
  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const handleCompareClick = async () => {
    if (!caseIdsInput.trim()) {
      toast({
        title: "Input required",
        description: "Please paste at least one Case ID.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Split by comma, newline, or space and filter out empty strings
    const caseIds = caseIdsInput.split(/[\s,]+/).filter(id => id.trim() !== '');

    setLoading(true);
    setComparisonResults([]);

    try {
      const response = await callApi(
        '/api/eval/coding/compare',
        accessToken,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ case_ids: caseIds })
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Create a deep, mutable copy to avoid potential "readonly property" errors during render.
        const mutableData = JSON.parse(JSON.stringify(data));
        setComparisonResults(mutableData);
        if (mutableData.length === 0) {
          toast({
            title: "No results",
            description: "No cases with multiple runs were found for the given IDs.",
            status: "info",
          });
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch comparison data');
      }
    } catch (error) {
      toast({
        title: "An error occurred",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderChanges = (changes) => {
    const { added, removed, modified } = changes;
    if (!added?.length && !removed?.length && !modified?.length) {
      return <Text>No changes</Text>;
    }

    return (
      <VStack align="start" spacing={1}>
        {added.length > 0 && <Text><strong>Added:</strong> {added.join(', ')}</Text>}
        {removed.length > 0 && <Text><strong>Removed:</strong> {removed.join(', ')}</Text>}
        {modified.map((mod, index) => (
          <Text key={index}>
            <strong>Modified:</strong> {mod.code} {mod.field} changed from "{String(mod.old_value)}" to "{String(mod.new_value)}"
          </Text>
        ))}
      </VStack>
    );
  };

  return (
    <Box>
      <HeaderMenus />
      <Box p={8}>
        <Heading mb={6}>Coding Run Comparison</Heading>

        <VStack mb={8} p={4} borderWidth="1px" borderRadius="lg" align="start" spacing={4}>
          <Text>Paste a list of Case IDs separated by commas, spaces, or new lines.</Text>
          <Textarea
            placeholder="Enter Case IDs here..."
            value={caseIdsInput}
            onChange={(e) => setCaseIdsInput(e.target.value)}
            rows={10}
          />
          <Button
            colorScheme="blue"
            onClick={handleCompareClick}
            isLoading={loading}
          >
            Compare Runs
          </Button>
        </VStack>

        {loading ? (
          <Flex justify="center"><Spinner /></Flex>
        ) : comparisonResults.length > 0 && (
          <>
            <HStack mb={4} spacing={4} align="center">
              <Text fontSize="lg" fontWeight="bold">
                {`Match Rate: ${((comparisonResults.filter(r => !r.has_changes).length / comparisonResults.length) * 100).toFixed(1)}%`}
                {` (${comparisonResults.filter(r => !r.has_changes).length}/${comparisonResults.length} cases)`}
              </Text>
              <Checkbox
                isChecked={hideMatches}
                onChange={(e) => setHideMatches(e.target.checked)}
              >
                Hide Matches
              </Checkbox>
            </HStack>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Case ID</Th>
                  <Th>External Case ID</Th>
                  <Th>External Patient ID</Th>
                  <Th>Run Count</Th>
                <Th>Changes?</Th>
                <Th>Details</Th>
                <Th>Coder</Th>
              </Tr>
            </Thead>
            <Tbody>
              {comparisonResults
                .filter(row => !hideMatches || row.has_changes)
                .map((row) => (
                <Tr key={row.case_id}>
                  <Td>{row.case_id}</Td>
                  <Td>{row.external_case_id}</Td>
                  <Td>{row.ionm_external_patient_id}</Td>
                  <Td>{row.run_count}</Td>
                  <Td>
                    <Badge colorScheme={row.has_changes ? 'red' : 'green'}>
                      {row.has_changes ? 'Yes' : 'No'}
                    </Badge>
                  </Td>
                  <Td>{renderChanges(row.changes)}</Td>
                  <Td>
                    {row.legacy_ionm_id ? (
                      <Link href={`/ionmform?caseid=${row.legacy_ionm_id}`} isExternal color="blue.500">
                        IONM Form
                      </Link>
                    ) : (
                      <Link href={`/full-cpt-coder?caseId=${row.external_case_id}`} isExternal color="blue.500">
                        Full CPT
                      </Link>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </>
        )}
      </Box>
    </Box>
  );
};

export default EvalCodingComparison;